import type { QuickReply, UpsertQuickReply } from '@dnx/contracts';
import { Alert, Button, Card, Field, Input, Spinner } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { quickRepliesQuery } from '../reception/queries.js';
import { SectionHeader } from './layout.js';

const EMPTY: UpsertQuickReply = { shortcut: '', title: '', body: '' };
const VARS = [['{nome}', 'primeiro nome do paciente'], ['{atendente}', 'seu primeiro nome'], ['{clinica}', 'nome da clínica']] as const;

function ReplyForm({ initial, id, onDone }: { initial: UpsertQuickReply; id?: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const set = (k: keyof UpsertQuickReply) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: () => api<QuickReply>(id ? `/quick-replies/${id}` : '/quick-replies', { method: id ? 'PATCH' : 'POST', body: form }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['quick-replies'] }); onDone(); },
  });
  const insert = (v: string) => setForm((f) => ({ ...f, body: `${f.body}${f.body && !f.body.endsWith(' ') ? ' ' : ''}${v}` }));
  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="grid gap-4 sm:grid-cols-[220px_1fr]">
        <Field label="Atalho" hint="Na conversa, digite / e o atalho.">
          <div className="flex items-center rounded-md border border-border-strong bg-surface focus-within:border-primary">
            <span className="pl-3 font-mono text-fg-subtle">/</span>
            <input value={form.shortcut} onChange={(e) => set('shortcut')(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              className="h-10 min-w-0 flex-1 bg-transparent pr-3 pl-1 font-mono text-sm text-fg outline-none" placeholder="boasvindas" maxLength={30} aria-label="Atalho" />
          </div>
        </Field>
        <Field label="Título"><Input value={form.title} onChange={(e) => set('title')(e.target.value)} placeholder="Boas-vindas" maxLength={80} /></Field>
      </div>
      <Field label="Texto">
        <textarea value={form.body} onChange={(e) => set('body')(e.target.value)} rows={4} maxLength={4096}
          placeholder="Olá, {nome}! Aqui é {atendente}, da {clinica}. Como posso ajudar?"
          className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
      </Field>
      <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-fg-muted">
        Variáveis:
        {VARS.map(([v, hint]) => (
          <button key={v} type="button" onClick={() => insert(v)} title={hint}
            className="rounded-md border border-border bg-surface-muted px-1.5 py-0.5 font-mono text-[12px] text-fg hover:border-border-strong">{v}</button>
        ))}
      </p>
      {save.error && <Alert tone="danger">{save.error.message}</Alert>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>Cancelar</Button>
        <Button type="submit" loading={save.isPending} disabled={!form.shortcut || form.title.trim().length < 2 || !form.body.trim()}>Salvar</Button>
      </div>
    </form>
  );
}

function ReplyRow({ q }: { q: QuickReply }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({ mutationFn: () => api<void>(`/quick-replies/${q.id}`, { method: 'DELETE' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['quick-replies'] }) });
  if (editing) return <li className="px-5 py-4"><ReplyForm initial={q} id={q.id} onDone={() => setEditing(false)} /></li>;
  return (
    <li className="flex flex-wrap items-start gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2"><code className="font-mono text-[13px] text-primary">/{q.shortcut}</code><span className="font-medium text-fg">{q.title}</span></p>
        <p className="mt-1 line-clamp-2 text-[13px] whitespace-pre-line text-fg-muted">{q.body}</p>
      </div>
      <div className="flex gap-1">
        {confirm ? (
          <>
            <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>Apagar</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>Manter</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}><Pencil className="size-4" />Editar</Button>
            <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirm(true)}><Trash2 className="size-4" /></Button>
          </>
        )}
      </div>
    </li>
  );
}

/** Configurações → Respostas rápidas: modelos que a equipe insere na conversa com "/atalho". */
export function QuickRepliesSettingsPage() {
  const { data, isLoading } = useQuery(quickRepliesQuery);
  const [creating, setCreating] = useState(false);
  return (
    <div>
      <SectionHeader title="Respostas rápidas" description="Textos prontos para as perguntas de sempre. Na conversa, digite / para escolher; as variáveis viram o nome do paciente, o seu e o da clínica."
        actions={!creating && <Button onClick={() => setCreating(true)}><Plus className="size-4" />Nova resposta</Button>} />
      {creating && <Card className="mt-6 p-5"><ReplyForm initial={EMPTY} onDone={() => setCreating(false)} /></Card>}
      {isLoading ? <Spinner className="mt-6 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
        <Card className="mt-4 overflow-hidden"><ul className="divide-y divide-border">{data.map((q) => <ReplyRow key={q.id} q={q} />)}</ul></Card>
      ) : !creating && <p className="mt-6 text-sm text-fg-muted">Nenhuma resposta rápida ainda.</p>}
    </div>
  );
}
